import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, MessageSquarePlus } from 'lucide-react';
import { useIntelliHealthChat } from '@/contexts/IntelliHealthChatContext';
import { usePatientRouteContext } from '@/hooks/usePatientRouteContext';
import MessageList, { type DisplayMessage } from './MessageList';
import ChatInput from './ChatInput';
import WelcomeState from './WelcomeState';
import ConnectionStatusBanner from './ConnectionStatus';
import ConversationSidebar from './ConversationSidebar';
// ChatFAB removed — IntelliHealth chat is now docked in the Three-Panel
// Console right panel (IntelliHealthRightPanel.tsx). The floating FAB is
// no longer rendered for the practitioner portal.

const IntelliHealthChatDrawer: React.FC = () => {
  const {
    isDrawerOpen,
    setDrawerOpen,
    messages,
    streamingMessage,
    isStreaming,
    isWaitingForResponse,
    connectionStatus,
    prompts,
    rateLimited,
    sendMessage,
    retry,
  } = useIntelliHealthChat();

  const { patientId, patientName, activeTab, encounterId } = usePatientRouteContext();

  // Track context-change system messages
  const [systemMessages, setSystemMessages] = useState<DisplayMessage[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const lastPatientIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastPatientIdRef.current === null) {
      lastPatientIdRef.current = patientId;
      return;
    }

    if (patientId !== lastPatientIdRef.current) {
      const text = patientName
        ? `Now viewing: ${patientName}`
        : patientId
          ? `Now viewing patient: ${patientId}`
          : 'No patient selected';

      setSystemMessages((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}`,
          type: 'system',
          content: text,
        },
      ]);
      lastPatientIdRef.current = patientId;
    }
  }, [patientId, patientName]);

  // When inside an active encounter, prepend a context tag to outgoing messages
  // so the IntelliHealth backend knows to scope its tools to this visit.
  const handleSend = useCallback(
    (text: string) => {
      const scopedTab = encounterId ? `encounter:${encounterId}` : (activeTab ?? undefined);
      sendMessage(text, patientId ?? undefined, scopedTab);
    },
    [sendMessage, patientId, activeTab, encounterId],
  );

  const inputDisabled = isStreaming || isWaitingForResponse || connectionStatus !== 'connected' || rateLimited;
  const showWelcome = messages.length === 0 && !isStreaming;

  const headerSubtitle = encounterId
    ? (patientName ? `\u00B7 ${patientName} \u00B7 This visit` : '\u00B7 This visit')
    : (patientName ? `\u00B7 ${patientName}` : null);

  // Encounter-aware quick chips. When in a visit, replace the generic
  // welcome prompts with visit-scoped actions; otherwise keep server-supplied
  // prompts. Falls back to the server prompts if those are richer.
  const encounterPrompts = encounterId ? [
    'Summarize this visit',
    'Draft AVS for this patient',
    'Check interactions for current meds',
    "What's changed since last visit?",
  ] : null;
  const effectivePrompts = encounterPrompts || prompts;

  return (
    <>
      {/* ChatFAB removed — replaced by docked IntelliHealthRightPanel */}

      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed inset-0 z-40 flex flex-col bg-white sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] sm:rounded-2xl sm:shadow-lg sm:border sm:border-slate-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-brand-dark-nav sm:rounded-t-2xl">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-5 w-5 text-brand-copper flex-shrink-0" />
                <h2 className="text-sm font-semibold text-white font-montserrat truncate">
                  IntelliHealth
                  {headerSubtitle && (
                    <span className="ml-1 text-xs font-normal text-slate-400">
                      {headerSubtitle}
                    </span>
                  )}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowSidebar((v) => !v)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Conversations"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors sm:hidden"
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Connection status */}
            <ConnectionStatusBanner status={connectionStatus} onRetry={retry} />

            {/* Rate limit message */}
            {rateLimited && (
              <div className="px-3 py-2 text-xs font-medium bg-amber-50 text-amber-700 border-b border-amber-200">
                Rate limit reached. Please wait before sending more messages.
              </div>
            )}

            {/* Content area with conversation sidebar overlay */}
            <div className="relative flex-1 flex flex-col overflow-hidden">
              {/* Conversation sidebar */}
              <ConversationSidebar
                isOpen={showSidebar}
                onClose={() => setShowSidebar(false)}
              />

              {/* Messages or welcome state */}
              {showWelcome ? (
                <div className="flex-1 overflow-y-auto">
                  <WelcomeState prompts={effectivePrompts} onSelectPrompt={handleSend} />
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  streamingMessage={streamingMessage}
                  isStreaming={isStreaming}
                  isWaitingForResponse={isWaitingForResponse}
                  systemMessages={systemMessages}
                />
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-3">
              <ChatInput
                onSend={handleSend}
                disabled={inputDisabled}
                placeholder={
                  rateLimited
                    ? 'Rate limit reached...'
                    : connectionStatus !== 'connected'
                      ? 'Waiting for connection...'
                      : undefined
                }
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default IntelliHealthChatDrawer;
